/**
 * CancelModal Component
 *
 * Modal for canceling a confirmed booking with penalty warning.
 */

import React, { useState } from 'react';

interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: (reason: string) => Promise<void>;
  isLoading: boolean;
  booking: {
    guestName: string;
    checkIn: Date;
    checkOut: Date;
    hostPayout: number;
  };
}

const CANCEL_REASONS = [
  { id: 'emergency', label: 'Personal emergency' },
  { id: 'property_damage', label: 'Property damage or maintenance issue' },
  { id: 'double_booking', label: 'Double booking error' },
  { id: 'safety_concern', label: 'Safety concern about the guest' },
  { id: 'force_majeure', label: 'Natural disaster or force majeure' },
  { id: 'other', label: 'Other reason' },
];

export const CancelModal: React.FC<CancelModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  isLoading,
  booking,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  if (!isOpen) return null;

  const handleCancel = async () => {
    if (!selectedReason || !acknowledged) return;
    await onCancel(selectedReason);
    onClose();
  };

  const handleClose = () => {
    setSelectedReason('');
    setAcknowledged(false);
    onClose();
  };

  const daysUntilCheckIn = Math.ceil(
    (booking.checkIn.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );

  const getPenaltyInfo = () => {
    if (daysUntilCheckIn <= 1) {
      return {
        severity: 'severe',
        message: 'Full payout will be refunded to guest. Your account may be penalized.',
        color: 'red',
      };
    } else if (daysUntilCheckIn <= 7) {
      return {
        severity: 'moderate',
        message: 'Full payout will be refunded to guest.',
        color: 'orange',
      };
    } else {
      return {
        severity: 'low',
        message: 'Full payout will be refunded to guest.',
        color: 'amber',
      };
    }
  };

  const penalty = getPenaltyInfo();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Cancel Booking</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-red-50 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">
                  This action cannot be undone
                </p>
                <p className="text-sm text-red-600 mt-1">{penalty.message}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Booking Details
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Guest</span>
                <span className="font-medium text-gray-900">{booking.guestName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Check-in</span>
                <span className="font-medium text-gray-900">
                  {booking.checkIn.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Time until check-in</span>
                <span className={`font-medium ${
                  daysUntilCheckIn <= 1 ? 'text-red-600' :
                  daysUntilCheckIn <= 7 ? 'text-orange-600' : 'text-gray-900'
                }`}>
                  {daysUntilCheckIn <= 0 ? 'Today' : `${daysUntilCheckIn} days`}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-500">Amount to refund</span>
                <span className="font-semibold text-red-600">
                  ${booking.hostPayout}
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Please select a reason for canceling:
          </p>

          <div className="space-y-2 mb-4">
            {CANCEL_REASONS.map((reason) => (
              <label
                key={reason.id}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  selectedReason === reason.id
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="cancelReason"
                  value={reason.id}
                  checked={selectedReason === reason.id}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-4 h-4 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">{reason.label}</span>
              </label>
            ))}
          </div>

          <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="w-4 h-4 mt-0.5 text-red-600 focus:ring-red-500 rounded"
            />
            <span className="text-sm text-gray-700">
              I understand that canceling will result in a full refund to the guest
              and may affect my host rating and listing visibility.
            </span>
          </label>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Keep Booking
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading || !selectedReason || !acknowledged}
            className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Canceling...' : 'Cancel Booking'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
      clipRule="evenodd"
    />
  </svg>
);

export default CancelModal;
